import type { GeoJsonFeatureCollection } from '@gpx-spa/geo-domain';
import {
  decodeGoogleRoutesEncodedPolyline,
  simplifyLineStringCoordinates,
} from '@gpx-spa/geo-adapters';
import { PATH_ALTERNATIVE_COLORS } from '@/utils/shipment-map-constants';

export type RouteSegmentForMap = {
  id: number;
  shipment_id: number;
  route_options: Array<{
    name?: string | null;
    encoded_polyline: string;
  }>;
};

/** Primary (first) option per segment — solid lines, distinct per segment. */
const SEGMENT_PRIMARY_COLORS = [
  '#2563eb',
  '#0d9488',
  '#7c3aed',
  '#c2410c',
  '#be185d',
];

/**
 * One LineString per **saved** route option: first option per segment is solid (primary);
 * further options are dashed alternates (same palette as shipment create).
 */
export function shipmentRouteSegmentsToFeatureCollection(
  segments: RouteSegmentForMap[],
  shipmentId: number,
  qualityPercent = 100,
): GeoJsonFeatureCollection {
  const forShipment = segments.filter((s) => s.shipment_id === shipmentId);
  const features: GeoJsonFeatureCollection['features'] = [];
  let segmentIndex = 0;

  for (const seg of forShipment) {
    const opts = seg.route_options ?? [];
    for (let optIdx = 0; optIdx < opts.length; optIdx++) {
      const opt = opts[optIdx];
      if (!opt?.encoded_polyline) continue;
      const lngLats = decodeGoogleRoutesEncodedPolyline(opt.encoded_polyline);
      if (!lngLats.length) continue;
      let coordinates = lngLats.map((c) => [c[0], c[1]] as [number, number]);
      if (qualityPercent < 100 && coordinates.length >= 2) {
        coordinates = simplifyLineStringCoordinates(
          coordinates,
          qualityPercent,
        );
      }
      const isPrimary = optIdx === 0;
      const lineColor = isPrimary
        ? SEGMENT_PRIMARY_COLORS[segmentIndex % SEGMENT_PRIMARY_COLORS.length]!
        : PATH_ALTERNATIVE_COLORS[
            (optIdx - 1) % PATH_ALTERNATIVE_COLORS.length
          ]!;
      const baseName = opt.name?.trim();
      const label = baseName
        ? `${baseName}${isPrimary ? '' : ` · alt ${optIdx}`}`
        : isPrimary
          ? `Segment ${seg.id}`
          : `Segment ${seg.id} · option ${optIdx + 1}`;
      const mapStyle: Record<string, unknown> = {
        lineColor,
        lineWidth: isPrimary ? 4 : 3,
        lineOpacity: 0.82,
        popup: label,
      };
      if (!isPrimary) {
        mapStyle.lineDashArray = [4, 6];
      }
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates },
        properties: { map: mapStyle },
      });
    }
    segmentIndex += 1;
  }
  return { type: 'FeatureCollection', features };
}
